// Multi-attachment gallery overlay.
//
// When a post has more than one attachment, the post renderer shows a
// "Gallery" link that calls galleryModal.open(attachments). The overlay dims
// the page (like the compose modal), lists every attachment as a thumbnail in
// a left column, and shows the selected one large in the center with a file
// link + size/dimensions underneath. Images, video, and audio play inline;
// anything we can't display (PDF, documents, unknown) gets a labelled
// placeholder.

import {
  truncateHash,
  formatBytes,
  mediaSizeBytes,
  mediaDimensions,
  preferredVideoPoster,
  isLikelyImageUrl,
  isLikelyVideoUrl,
  urlPathSansQuery,
  mediaFullUrl,
  safeUrl,
  isPdfAttachment,
} from "./media.js";

// Video loop/mute defaults come from the user settings store, which lives in
// app.js — importing it here would be circular, so app.js installs a getter at
// boot instead. Called each time the stage is built, so a settings change
// takes effect on the next attachment shown.
let videoDefaults = () => ({ loop: false, muted: false });
export function setGalleryVideoDefaults(fn) { videoDefaults = fn; }

// Classify an attachment for gallery rendering. The Mastodon/Mitra `type`
// field is authoritative when present; otherwise sniff the URL. Anything we
// can't show inline (PDF, documents, unknown) is "other" and falls back to a
// labelled placeholder in both the thumbnail column and the stage.
function galleryMediaKind(media) {
  const t = media?.type;
  if (t === "image") return "image";
  if (t === "video" || t === "gifv") return "video";
  if (t === "audio") return "audio";
  if (isPdfAttachment(media)) return "pdf";
  const url = mediaFullUrl(media);
  if (isLikelyImageUrl(url)) return "image";
  if (isLikelyVideoUrl(url)) return "video";
  if (/\.(mp3|m4a|aac|oga|ogg|wav|flac|opus)$/i.test(urlPathSansQuery(url))) return "audio";
  return "other";
}

function galleryExtLabel(media) {
  const path = urlPathSansQuery(mediaFullUrl(media));
  const dot = path.lastIndexOf(".");
  if (dot >= 0 && dot < path.length - 1) return path.slice(dot + 1).toUpperCase();
  return media?.mime_type || "";
}

// A non-displayable / iconographic stand-in (audio, PDF, video w/o poster).
function galleryPlaceholder(icon, media) {
  const ph = document.createElement("div");
  ph.className = "gallery-placeholder";
  const ic = document.createElement("i");
  ic.className = `fa-solid ${icon}`;
  ph.appendChild(ic);
  const ext = galleryExtLabel(media);
  if (ext) {
    const label = document.createElement("span");
    label.className = "gallery-placeholder-ext";
    label.textContent = ext;
    ph.appendChild(label);
  }
  return ph;
}

function galleryThumbImage(src) {
  const img = document.createElement("img");
  img.className = "gallery-thumb-img";
  img.alt = "";
  img.loading = "lazy";
  if (src) img.src = src;
  return img;
}

// Left-column thumbnail for one attachment.
function galleryThumbNode(media) {
  const kind = galleryMediaKind(media);
  if (kind === "image") return galleryThumbImage(media?.preview_url || media?.url || "");
  if (kind === "video") {
    const poster = preferredVideoPoster(media);
    const wrap = document.createElement("span");
    wrap.className = "gallery-thumb-video";
    wrap.appendChild(poster ? galleryThumbImage(poster) : galleryPlaceholder("fa-film", media));
    const play = document.createElement("i");
    play.className = "fa-solid fa-play gallery-thumb-play";
    wrap.appendChild(play);
    return wrap;
  }
  if (kind === "audio") return galleryPlaceholder("fa-music", media);
  if (kind === "pdf") return galleryPlaceholder("fa-file-pdf", media);
  return galleryPlaceholder("fa-file", media);
}

// Center-stage (expanded) rendering for one attachment.
function galleryStageNode(media) {
  const kind = galleryMediaKind(media);
  const full = mediaFullUrl(media);
  if (kind === "image") {
    const img = document.createElement("img");
    img.className = "gallery-stage-img";
    img.alt = "";
    if (full) img.src = full;
    return img;
  }
  if (kind === "video") {
    const v = document.createElement("video");
    v.className = "gallery-stage-video";
    v.controls = true;
    const { loop, muted } = videoDefaults();
    v.loop = !!loop;
    v.muted = !!muted;
    v.playsInline = true;
    v.preload = "metadata";
    const poster = preferredVideoPoster(media);
    if (poster) v.poster = poster;
    if (full) v.src = full;
    return v;
  }
  if (kind === "audio") {
    const wrap = document.createElement("div");
    wrap.className = "gallery-stage-audio";
    const ph = galleryPlaceholder("fa-music", media);
    ph.classList.add("gallery-stage-placeholder");
    wrap.appendChild(ph);
    const a = document.createElement("audio");
    a.controls = true;
    a.preload = "metadata";
    if (full) a.src = full;
    wrap.appendChild(a);
    return wrap;
  }
  // Documents open from the file link below the stage.
  const ph = galleryPlaceholder(kind === "pdf" ? "fa-file-pdf" : "fa-file", media);
  ph.classList.add("gallery-stage-placeholder");
  return ph;
}

class GalleryModal {
  constructor() {
    this.el       = document.getElementById("gallery-modal");
    this.thumbs   = document.getElementById("gallery-thumbs");
    this.stage    = document.getElementById("gallery-stage");
    this.caption  = document.getElementById("gallery-caption");
    this.fileinfo = document.getElementById("gallery-fileinfo");
    this.closeBtn = document.getElementById("gallery-modal-close");
    this._items = [];
    this._activeIndex = 0;
    this._wireEvents();
  }

  _wireEvents() {
    this.closeBtn.addEventListener("click", () => this.close());
    this.el.addEventListener("click", (e) => {
      if (e.target === this.el) this.close();   // backdrop click
    });
    document.addEventListener("keydown", (e) => {
      if (!this.isOpen()) return;
      if (e.key === "Escape") { this.close(); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        this._select(this._activeIndex + 1);
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        this._select(this._activeIndex - 1);
      }
    });
  }

  open(attachments, startIndex = 0) {
    this._items = (attachments || []).filter(Boolean);
    if (!this._items.length) return;
    this._renderThumbs();
    this._select(startIndex);
    this.el.hidden = false;
    this.el.classList.add("is-open");
  }

  close() {
    this.el.classList.remove("is-open");
    this.el.hidden = true;
    this.stage.innerHTML = "";    // detaches any playing <video>/<audio>
    this.thumbs.innerHTML = "";
    this.caption.textContent = "";
    this.caption.hidden = true;
    this.fileinfo.innerHTML = "";
    this._items = [];
  }

  isOpen() {
    return this.el.classList.contains("is-open");
  }

  _renderThumbs() {
    this.thumbs.innerHTML = "";
    this._items.forEach((media, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "gallery-thumb";
      btn.appendChild(galleryThumbNode(media));
      btn.addEventListener("click", () => this._select(i));
      this.thumbs.appendChild(btn);
    });
  }

  _select(index) {
    const n = this._items.length;
    if (!n) return;
    // wrap around so arrow-key navigation is continuous
    this._activeIndex = ((index % n) + n) % n;
    const media = this._items[this._activeIndex];

    Array.from(this.thumbs.children).forEach((c, i) => {
      c.classList.toggle("is-active", i === this._activeIndex);
    });

    this.stage.innerHTML = "";
    this.stage.appendChild(galleryStageNode(media));

    // Alt-text / caption for the attachment (Mastodon `description`).
    // Shown above the file info line; hidden when absent.
    const desc = (media?.description || "").trim();
    if (desc) {
      this.caption.textContent = desc;
      this.caption.hidden = false;
    } else {
      this.caption.textContent = "";
      this.caption.hidden = true;
    }

    const full = mediaFullUrl(media);
    const rawName = (urlPathSansQuery(full).split("/").pop()) || "file";
    this.fileinfo.innerHTML = "";
    this.fileinfo.appendChild(document.createTextNode("File: "));
    const a = document.createElement("a");
    a.className = "file-link";
    a.href = safeUrl(full) || "#";
    a.target = "_blank";
    a.rel = "noopener";
    a.textContent = truncateHash(rawName, 48);
    this.fileinfo.appendChild(a);
    const parts = [formatBytes(mediaSizeBytes(media)), mediaDimensions(media)].filter(Boolean);
    if (parts.length) {
      this.fileinfo.appendChild(document.createTextNode(` (${parts.join(", ")})`));
    }
  }
}

export const galleryModal = new GalleryModal();
